import { CACHE_KEY, useCache } from '@/hooks/web/useCache'
import { ElementPlusSize } from '@/types/elementPlus'
import { LayoutType } from '@/types/layout'
import { ThemeTypes } from '@/types/theme'
import { humpToUnderline, setCssVar } from '@/utils'
import { colorIsDark, getCssColorVariable, hexToRGB, lighten, mix } from '@/utils/color'
import { normalizeLayout } from '@/utils/layout'
import { ElMessage } from 'element-plus'
import { defineStore } from 'pinia'
import { store } from '../index'

const { wsCache } = useCache()

const LIGHT_MODE_CSS_VARS: Record<string, string> = {
  '--el-border-color': 'transparent',
  '--el-border-color-light': 'transparent',
  '--el-border-color-lighter': 'transparent',
  '--el-border-color-extra-light': 'transparent',
  '--el-border-color-dark': 'transparent',
  '--el-border-color-darker': 'transparent',
  '--input-bg-color': '#F8F8F8',
  '--breadcrumb-bg-color': '#FFFFFF',
  '--tags-view-bg-color': '#FFFFFF',
  '--tag-item-bg-color': '#F5F6F8',
  '--form-section-bg': '#FAFBFC',
  '--btn-default-bg': '#F2F3F5',
  '--dropdown-panel-bg': '#FAFBFC',
  '--app-content-bg-color': '#FFFFFF',
  '--app-content-text-color': '#171A1D',
  '--app-content-secondary-text-color': '#646A73',
  '--app-content-muted-text-color': '#8A8F98',
  '--app-card-bg-color': '#ffffff',
  '--app-card-border-color': '#EBEDF0',
  '--home-slide-1-bg': 'linear-gradient(135deg, #f0edff 0%, #ede7fd 60%, #e8f0fe 100%)',
  '--home-slide-2-bg': 'linear-gradient(135deg, #eff6ff 0%, #f0edff 60%, #ecfdf5 100%)',
  '--home-slide-3-bg': 'linear-gradient(135deg, #fff7e6 0%, #fffbeb 60%, #fef2f2 100%)',
  '--home-carousel-arrow-bg': 'rgba(255,255,255,0.9)',
  '--home-carousel-arrow-border': 'rgba(0,0,0,0.05)',
  '--home-renewal-bg': 'linear-gradient(135deg, #fff7e6, #fffbeb)',
  '--home-renewal-border-color': '#fde9c8',
  '--home-renewal-title-color': '#b8740a',
  '--home-renewal-divider-color': '#f3d9b0',
  '--home-renewal-note-color': '#c79a5e',
  '--home-renewal-button-bg': '#ffffff',
  '--home-renewal-button-text-color': '#d98e00',
  '--home-renewal-button-border-color': '#f0c98a'
}

const DARK_MODE_CSS_VARS: Record<string, string> = {
  '--el-border-color': 'rgba(255,255,255,0.08)',
  '--el-border-color-light': 'rgba(255,255,255,0.08)',
  '--el-border-color-lighter': 'rgba(255,255,255,0.06)',
  '--el-border-color-extra-light': 'rgba(255,255,255,0.04)',
  '--el-border-color-dark': 'rgba(255,255,255,0.12)',
  '--el-border-color-darker': 'rgba(255,255,255,0.16)',
  '--input-bg-color': '#202124',
  '--breadcrumb-bg-color': '#1F2024',
  '--tags-view-bg-color': '#19191C',
  '--tag-item-bg-color': '#24252A',
  '--form-section-bg': '#19191C',
  '--btn-default-bg': '#24252A',
  '--dropdown-panel-bg': '#1F2024',
  '--app-content-bg-color': '#111114',
  '--app-content-text-color': 'rgba(255,255,255,0.88)',
  '--app-content-secondary-text-color': 'rgba(255,255,255,0.66)',
  '--app-content-muted-text-color': 'rgba(255,255,255,0.48)',
  '--app-card-bg-color': '#19191C',
  '--app-card-border-color': 'rgba(255,255,255,0.08)',
  '--home-slide-1-bg': 'linear-gradient(135deg, #252238 0%, #1c1d29 60%, #172033 100%)',
  '--home-slide-2-bg': 'linear-gradient(135deg, #182338 0%, #1f1d32 60%, #14251f 100%)',
  '--home-slide-3-bg': 'linear-gradient(135deg, #2a2115 0%, #211d16 60%, #2a1818 100%)',
  '--home-carousel-arrow-bg': 'rgba(36,37,42,0.92)',
  '--home-carousel-arrow-border': 'rgba(255,255,255,0.08)',
  '--home-renewal-bg': 'linear-gradient(135deg, #2a2115, #1d1a15)',
  '--home-renewal-border-color': 'rgba(251,191,36,0.22)',
  '--home-renewal-title-color': '#fbbf24',
  '--home-renewal-divider-color': 'rgba(251,191,36,0.18)',
  '--home-renewal-note-color': 'rgba(251,191,36,0.7)',
  '--home-renewal-button-bg': '#24252A',
  '--home-renewal-button-text-color': '#fbbf24',
  '--home-renewal-button-border-color': 'rgba(251,191,36,0.3)',
  '--left-menu-bg-color': '#19191C',
  '--left-menu-bg-light-color': '#202124',
  '--left-menu-bg-active-color': 'rgba(var(--el-color-primary-rgb),0.22)',
  '--left-menu-collapse-bg-active-color': 'rgba(var(--el-color-primary-rgb),0.22)',
  '--left-menu-text-color': 'rgba(255,255,255,0.72)',
  '--left-menu-text-active-color': '#FFFFFF',
  '--left-menu-border-color': 'rgba(255,255,255,0.08)',
  '--logo-title-text-color': '#FFFFFF',
  '--logo-border-color': 'rgba(255,255,255,0.08)',
  '--top-header-bg-color': '#19191C',
  '--top-header-text-color': 'rgba(255,255,255,0.82)',
  '--top-header-hover-color': '#24252A',
  '--top-tool-border-color': 'rgba(255,255,255,0.08)'
}

interface AppState {
  breadcrumb: boolean
  breadcrumbIcon: boolean
  collapse: boolean
  uniqueOpened: boolean
  hamburger: boolean
  screenfull: boolean
  search: boolean
  size: boolean
  locale: boolean
  message: boolean
  im: boolean
  tagsView: boolean
  tagsViewImmerse: boolean
  tagsViewIcon: boolean
  logo: boolean
  fixedHeader: boolean
  greyMode: boolean
  pageLoading: boolean
  layout: LayoutType
  title: string
  userInfo: string
  isDark: boolean
  currentSize: ElementPlusSize
  sizeMap: ElementPlusSize[]
  mobile: boolean
  footer: boolean
  theme: ThemeTypes
  fixedMenu: boolean
  headerTabKey: string
}

export const useAppStore = defineStore('app', {
  state: (): AppState => {
    return {
      userInfo: 'userInfo', // 登录信息存储字段-建议每个项目换一个字段，避免与其他项目冲突
      sizeMap: ['default', 'large', 'small'],
      mobile: false, // 是否是移动端
      title: import.meta.env.VITE_APP_TITLE, // 标题
      pageLoading: false, // 路由跳转loading

      breadcrumb: true, // 面包屑
      breadcrumbIcon: true, // 面包屑图标
      collapse: false, // 折叠菜单
      uniqueOpened: true, // 是否只保持一个子菜单的展开
      hamburger: true, // 折叠图标
      screenfull: true, // 全屏图标
      search: true, // 搜索图标
      size: true, // 尺寸图标
      locale: true, // 多语言图标
      message: true, // 消息图标
      im: true, //IM即时通讯图标
      tagsView: true, // 标签页
      tagsViewImmerse: false, // 标签页沉浸
      tagsViewIcon: true, // 是否显示标签图标
      logo: true, // logo
      fixedHeader: true, // 固定toolheader
      footer: true, // 显示页脚
      greyMode: false, // 是否开始灰色模式，用于特殊悼念日
      fixedMenu: wsCache.get('fixedMenu') || false, // 是否固定菜单
      headerTabKey: wsCache.get('headerTabKey') || 'workbench', // 顶部导航当前选中项

      layout: normalizeLayout(wsCache.get(CACHE_KEY.LAYOUT)), // layout布局
      isDark: wsCache.get(CACHE_KEY.IS_DARK) || false, // 是否是暗黑模式
      currentSize: wsCache.get('default') || 'default', // 组件尺寸
      theme: (() => {
        // 浅色侧栏钉钉风默认值；缓存缺 hover 等字段时补齐，避免旧缓存盖住新样式
        const dingtalkLightMenu = {
          elColorPrimary: '#409eff',
          leftMenuBorderColor: 'transparent',
          leftMenuBgColor: '#F5F5F7',
          leftMenuBgLightColor: '#F5F5F7',
          leftMenuBgActiveColor: '#FFFFFF',
          leftMenuBgHoverColor: '#E8F3FF',
          leftMenuCollapseBgActiveColor: '#FFFFFF',
          leftMenuTextColor: '#171A1D',
          leftMenuTextActiveColor: '#171A1D',
          logoTitleTextColor: '#171A1D',
          logoBorderColor: 'transparent',
          topHeaderBgColor: '#FFFFFF',
          topHeaderTextColor: '#171A1D',
          topHeaderHoverColor: '#F5F5F7',
          topToolBorderColor: 'transparent'
        }
        const cached = wsCache.get(CACHE_KEY.THEME) as ThemeTypes | undefined
        let next: ThemeTypes
        if (!cached) {
          next = { ...dingtalkLightMenu }
        } else {
          const menuBg = cached.leftMenuBgColor || dingtalkLightMenu.leftMenuBgColor
          if (!colorIsDark(menuBg)) {
            // 浅色侧栏强制钉钉交互色（选中白 / 悬停淡蓝 / 近黑字），保留用户主题色等其它项
            next = {
              ...dingtalkLightMenu,
              ...cached,
              leftMenuBgColor: cached.leftMenuBgColor || dingtalkLightMenu.leftMenuBgColor,
              leftMenuBgLightColor:
                cached.leftMenuBgLightColor ||
                cached.leftMenuBgColor ||
                dingtalkLightMenu.leftMenuBgLightColor,
              leftMenuBgHoverColor: dingtalkLightMenu.leftMenuBgHoverColor,
              leftMenuBgActiveColor: dingtalkLightMenu.leftMenuBgActiveColor,
              leftMenuCollapseBgActiveColor: dingtalkLightMenu.leftMenuCollapseBgActiveColor,
              leftMenuTextColor: dingtalkLightMenu.leftMenuTextColor,
              leftMenuTextActiveColor: dingtalkLightMenu.leftMenuTextActiveColor
            }
          } else {
            next = {
              ...cached,
              leftMenuBgHoverColor: cached.leftMenuBgHoverColor || 'rgba(255,255,255,0.08)'
            }
          }
        }
        wsCache.set(CACHE_KEY.THEME, next)
        return next
      })()
    }
  },
  getters: {
    getBreadcrumb(): boolean {
      return this.breadcrumb
    },
    getBreadcrumbIcon(): boolean {
      return this.breadcrumbIcon
    },
    getCollapse(): boolean {
      return this.collapse
    },
    getUniqueOpened(): boolean {
      return this.uniqueOpened
    },
    getHamburger(): boolean {
      return this.hamburger
    },
    getScreenfull(): boolean {
      return this.screenfull
    },
    getSearch(): boolean {
      return this.search
    },
    getSize(): boolean {
      return this.size
    },
    getLocale(): boolean {
      return this.locale
    },
    getMessage(): boolean {
      return this.message
    },
    getIm(): boolean {
      return this.im
    },
    getTagsView(): boolean {
      return this.tagsView
    },
    getTagsViewImmerse(): boolean {
      return this.tagsViewImmerse
    },
    getTagsViewIcon(): boolean {
      return this.tagsViewIcon
    },
    getLogo(): boolean {
      return this.logo
    },
    getFixedHeader(): boolean {
      return this.fixedHeader
    },
    getGreyMode(): boolean {
      return this.greyMode
    },
    getFixedMenu(): boolean {
      return this.fixedMenu
    },
    getHeaderTabKey(): string {
      return this.headerTabKey
    },
    getPageLoading(): boolean {
      return this.pageLoading
    },
    getLayout(): LayoutType {
      return this.layout
    },
    getTitle(): string {
      return this.title
    },
    getUserInfo(): string {
      return this.userInfo
    },
    getIsDark(): boolean {
      return this.isDark
    },
    getCurrentSize(): ElementPlusSize {
      return this.currentSize
    },
    getSizeMap(): ElementPlusSize[] {
      return this.sizeMap
    },
    getMobile(): boolean {
      return this.mobile
    },
    getTheme(): ThemeTypes {
      return this.theme
    },
    getFooter(): boolean {
      return this.footer
    }
  },
  actions: {
    setPrimaryLight() {
      if (this.theme.elColorPrimary) {
        const elColorPrimary = this.theme.elColorPrimary
        const color = this.isDark ? '#000000' : '#ffffff'
        const lightList = [3, 5, 7, 8, 9]
        lightList.forEach((v) => {
          setCssVar(`--el-color-primary-light-${v}`, mix(color, elColorPrimary, v / 10))
        })
        setCssVar(`--el-color-primary-dark-2`, mix(color, elColorPrimary, 0.2))

        this.setAllColorRgbVars()
      }
    },

    // 处理element自带的主题色和辅助色的-rgb切换主题变化，如：--el-color-primary-rgb
    setAllColorRgbVars() {
      // 需要处理的颜色类型列表
      const colorTypes = ['primary', 'success', 'warning', 'danger', 'error', 'info']

      colorTypes.forEach((type) => {
        // 获取当前颜色值
        const colorValue = getCssColorVariable(`--el-color-${type}`)
        if (colorValue) {
          // 转换为rgba并提取RGB部分
          const rgbaString = hexToRGB(colorValue, 1)
          const rgbValues = rgbaString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
          if (rgbValues) {
            const [, r, g, b] = rgbValues
            // 设置对应的RGB变量
            setCssVar(`--el-color-${type}-rgb`, `${r}, ${g}, ${b}`)
          }
        }
      })
    },
    setBreadcrumb(breadcrumb: boolean) {
      this.breadcrumb = breadcrumb
    },
    setBreadcrumbIcon(breadcrumbIcon: boolean) {
      this.breadcrumbIcon = breadcrumbIcon
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse
    },
    setUniqueOpened(uniqueOpened: boolean) {
      this.uniqueOpened = uniqueOpened
    },
    setHamburger(hamburger: boolean) {
      this.hamburger = hamburger
    },
    setScreenfull(screenfull: boolean) {
      this.screenfull = screenfull
    },
    setSearch(search: boolean) {
      this.search = search
    },
    setSize(size: boolean) {
      this.size = size
    },
    setLocale(locale: boolean) {
      this.locale = locale
    },
    setMessage(message: boolean) {
      this.message = message
    },
    setIm(im: boolean) {
      this.im = im
    },
    setTagsView(tagsView: boolean) {
      this.tagsView = tagsView
    },
    setTagsViewImmerse(tagsViewImmerse: boolean) {
      this.tagsViewImmerse = tagsViewImmerse
    },
    setTagsViewIcon(tagsViewIcon: boolean) {
      this.tagsViewIcon = tagsViewIcon
    },
    setLogo(logo: boolean) {
      this.logo = logo
    },
    setFixedHeader(fixedHeader: boolean) {
      this.fixedHeader = fixedHeader
    },
    setGreyMode(greyMode: boolean) {
      this.greyMode = greyMode
    },
    setFixedMenu(fixedMenu: boolean) {
      wsCache.set('fixedMenu', fixedMenu)
      this.fixedMenu = fixedMenu
    },
    setHeaderTabKey(headerTabKey: string) {
      wsCache.set('headerTabKey', headerTabKey)
      this.headerTabKey = headerTabKey
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading
    },
    setLayout(layout: LayoutType) {
      const normalizedLayout = normalizeLayout(layout)
      if (this.mobile && normalizedLayout !== 'sidebar-nav') {
        ElMessage.warning('移动端模式下不支持切换其他布局')
        return
      }
      this.layout = normalizedLayout
      wsCache.set(CACHE_KEY.LAYOUT, this.layout)
    },
    setTitle(title: string) {
      this.title = title
    },
    setIsDark(isDark: boolean, persist = true) {
      this.isDark = isDark
      if (this.isDark) {
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
      } else {
        document.documentElement.classList.add('light')
        document.documentElement.classList.remove('dark')
      }
      if (persist) {
        wsCache.set(CACHE_KEY.IS_DARK, this.isDark)
      }
      this.setCssVarTheme()
    },
    setCurrentSize(currentSize: ElementPlusSize) {
      this.currentSize = currentSize
      wsCache.set('currentSize', this.currentSize)
    },
    setMobile(mobile: boolean) {
      this.mobile = mobile
    },
    setTheme(theme: ThemeTypes) {
      this.theme = Object.assign(this.theme, theme)
      wsCache.set(CACHE_KEY.THEME, this.theme)
    },
    setModeCssVars() {
      const modeVars = this.isDark ? DARK_MODE_CSS_VARS : LIGHT_MODE_CSS_VARS
      for (const key in modeVars) {
        setCssVar(key, modeVars[key])
      }
    },
    setCssVarTheme() {
      for (const key in this.theme) {
        setCssVar(`--${humpToUnderline(key)}`, this.theme[key])
      }
      const topHeaderBgColor = this.theme.leftMenuBgColor || '#ffffff'
      const isDarkColor = colorIsDark(topHeaderBgColor)
      setCssVar('--top-header-bg-color', topHeaderBgColor)
      setCssVar(
        '--top-header-text-color',
        isDarkColor ? '#fff' : this.theme.topHeaderTextColor || '#171A1D'
      )
      setCssVar(
        '--top-header-hover-color',
        isDarkColor ? lighten(topHeaderBgColor, 6) : this.theme.topHeaderHoverColor || '#F5F5F7'
      )
      // 悬停色兜底：浅色侧栏淡蓝，深色侧栏半透明白
      setCssVar(
        '--left-menu-bg-hover-color',
        this.theme.leftMenuBgHoverColor || (isDarkColor ? 'rgba(255,255,255,0.08)' : '#E8F3FF')
      )
      this.setModeCssVars()
      this.setPrimaryLight()
    },
    setFooter(footer: boolean) {
      this.footer = footer
    }
  },
  persist: false
})

export const useAppStoreWithOut = () => {
  return useAppStore(store)
}
